import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Info, Play, Pause, Images, Map, Grid3x3, Maximize2, Minimize2, Volume2, VolumeX,
  Share2, Settings, X, ChevronLeft, ChevronRight, Compass, Plus, Minus, RotateCw,
  ArrowLeft, Phone, Loader2, Calendar,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Logo } from "../components/Logo";
import api from "../lib/api";
import { formatPrice, typeLabel, img as imgFallback } from "../lib/utils-app";
import { toast } from "sonner";

const W = (id, u = false) =>
  u
    ? `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=90&w=1920`
    : `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2`;

const ROOMS = [
  { en: "Living Room", es: "Sala de Estar" },
  { en: "Chef's Kitchen", es: "Cocina Gourmet" },
  { en: "Master Suite", es: "Suite Principal" },
  { en: "Terrace & Pool", es: "Terraza y Piscina" },
  { en: "Bedroom", es: "Habitación" },
  { en: "Exterior", es: "Exterior" },
];

const POS = [
  { x: 38, y: 20, w: 26, h: 34 }, { x: 66, y: 20, w: 24, h: 30 },
  { x: 66, y: 55, w: 24, h: 34 }, { x: 10, y: 55, w: 26, h: 34 },
  { x: 38, y: 58, w: 26, h: 30 }, { x: 10, y: 12, w: 24, h: 30 },
];

const OVERFLOW = 0.5;

export default function VirtualTour() {
  const { lang } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [propData, setPropData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(!!id);

  const [idx, setIdx] = useState(0);
  const [offset, setOffset] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [auto, setAuto] = useState(true);
  const [showInfo, setShowInfo] = useState(true);
  const [panel, setPanel] = useState(null);
  const [sound, setSound] = useState(false);
  const [fs, setFs] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "10:00" });
  const [submitting, setSubmitting] = useState(false);

  const containerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startOffset: 0 });
  const rafRef = useRef(null);
  const dirRef = useRef(-1);

  const L = (o) => (o ? o[lang] || o.en : "");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/properties/${id}`).then((r) => setPropData(r.data)).catch(() => setPropData(null)).finally(() => setLoading(false));
  }, [id]);

  // Active, uploaded listings available to tour (for the selector).
  useEffect(() => {
    api.get("/properties?limit=100")
      .then((r) => setListings((r.data.items || []).filter((p) => (p.status || "active") === "active" && (p.images || []).length)))
      .catch(() => {});
  }, []);

  const scenes = useMemo(() => {
    if (!propData) return [];
    const imgs = (propData.images || []).slice(0, 6);
    if (!imgs.length) return [];
    const sc = imgs.map((im, i) => ({ id: `s${i}`, name: ROOMS[i % ROOMS.length], img: im, hotspots: [] }));
    sc.forEach((s, i) => {
      if (sc.length > 1) {
        s.hotspots.push({ to: sc[(i + 1) % sc.length].id, x: 60, y: 54 });
        if (sc.length > 2) s.hotspots.push({ to: sc[(i - 1 + sc.length) % sc.length].id, x: 30, y: 58 });
      }
    });
    return sc;
  }, [propData]);

  const residence = useMemo(() => {
    if (!propData) return null;
    return {
      name: L(propData.title),
      tag: `${typeLabel(propData.property_type, lang)} · ${propData.neighborhood}, ${propData.city}`,
      desc: L(propData.description) || (lang === "es"
        ? "Recorre cada espacio como si estuvieras dentro. Arrastra para mirar alrededor y usa los marcadores para navegar."
        : "Walk through every space as if you were inside. Drag to look around and use the markers to navigate."),
      price: formatPrice(propData.price, propData.currency),
      listingId: propData.id,
    };
  }, [propData, lang]); // eslint-disable-line

  useEffect(() => { setIdx(0); setOffset(0); setZoom(1); }, [scenes]);

  const scene = scenes[Math.min(idx, scenes.length - 1)];

  const bounds = useCallback(() => {
    const c = containerRef.current;
    if (!c) return 0;
    return -(c.clientWidth * OVERFLOW * zoom);
  }, [zoom]);

  const goScene = useCallback((to) => {
    const i = scenes.findIndex((s) => s.id === to);
    if (i >= 0) { setIdx(i); setOffset(0); setZoom(1); }
  }, [scenes]);

  useEffect(() => {
    if (!auto) { cancelAnimationFrame(rafRef.current); return; }
    let last = performance.now();
    const speed = 22;
    const loop = (t) => {
      const dt = Math.min((t - last) / 1000, 0.05); last = t;
      setOffset((o) => {
        let n = o + dirRef.current * speed * dt;
        const min = bounds();
        if (n <= min) { n = min; dirRef.current = 1; }
        if (n >= 0) { n = 0; dirRef.current = -1; }
        return n;
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [auto, bounds, idx]);

  const onDown = (e) => { setAuto(false); dragRef.current = { active: true, startX: e.clientX, startOffset: offset }; };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    let n = d.startOffset + (e.clientX - d.startX);
    n = Math.max(bounds(), Math.min(0, n));
    setOffset(n);
  };
  const onUp = () => { dragRef.current.active = false; };

  const toggleFs = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen?.(); setFs(true); }
    else { document.exitFullscreen?.(); setFs(false); }
  };
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success(lang === "es" ? "Enlace copiado" : "Link copied"); };

  const submitVisit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/appointments", { ...form, property_id: residence.listingId, agent_id: propData?.agent_id || null, type: "tour", notes: "Requested from virtual tour" });
      toast.success(lang === "es" ? "¡Visita solicitada! Revisa tu correo." : "Visit requested! Check your email.");
      setPanel(null);
      setForm({ name: "", email: "", phone: "", date: "", time: "10:00" });
    } catch { toast.error(lang === "es" ? "No se pudo enviar. Intenta de nuevo." : "Could not send. Try again."); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="fixed inset-0 z-[80] bg-[#05070d] grid place-items-center"><Loader2 className="animate-spin text-brandgreen" size={36} /></div>;
  }
  if (!scene) {
    return (
      <div className="fixed inset-0 z-[80] bg-[#05070d] text-white grid place-items-center px-6" data-testid="tour-empty">
        <div className="max-w-md w-full text-center">
          <Link to="/" className="inline-block bg-white/95 rounded-sm px-3 py-1.5 mb-6"><Logo className="h-8" /></Link>
          <h1 className="font-serif text-3xl mb-2">{lang === "es" ? "Tour Virtual 360°" : "360° Virtual Tour"}</h1>
          <p className="text-white/70 text-sm mb-6">
            {listings.length
              ? (lang === "es" ? "Elige una propiedad para iniciar el recorrido." : "Choose a property to start the tour.")
              : (lang === "es" ? "Aún no hay propiedades disponibles para recorrer." : "No properties available to tour yet.")}
          </p>
          {listings.length > 0 && (
            <select defaultValue="" onChange={(e) => e.target.value && navigate(`/virtual-tour/${e.target.value}`)}
              className="w-full bg-white/10 border border-white/25 text-white px-4 py-3 rounded-sm focus:border-brandgreen outline-none [color-scheme:dark]" data-testid="tour-empty-select">
              <option value="" disabled>{lang === "es" ? "Elige una propiedad…" : "Choose a property…"}</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{L(l.title)}</option>)}
            </select>
          )}
          <Link to="/buy" className="inline-block mt-6 text-xs tracking-[0.2em] uppercase text-brandgreen hover:underline" data-testid="tour-browse-all">
            {lang === "es" ? "Ver todas las propiedades" : "Browse all properties"}
          </Link>
        </div>
      </div>
    );
  }

  const cmd = [
    { key: "info", icon: Info, on: showInfo, action: () => setShowInfo((v) => !v), label: lang === "es" ? "Información" : "Info" },
    { key: "auto", icon: auto ? Pause : Play, on: auto, action: () => setAuto((v) => !v), label: lang === "es" ? "Auto-rotar" : "Auto-rotate" },
    { key: "scenes", icon: Grid3x3, action: () => setPanel(panel === "scenes" ? null : "scenes"), label: lang === "es" ? "Espacios" : "Rooms" },
    { key: "gallery", icon: Images, action: () => setPanel(panel === "gallery" ? null : "gallery"), label: lang === "es" ? "Galería" : "Gallery" },
    { key: "floorplan", icon: Map, action: () => setPanel(panel === "floorplan" ? null : "floorplan"), label: lang === "es" ? "Plano" : "Floor plan" },
    { key: "sound", icon: sound ? Volume2 : VolumeX, on: sound, action: () => setSound((v) => !v), label: lang === "es" ? "Ambiente" : "Ambience" },
    { key: "fs", icon: fs ? Minimize2 : Maximize2, action: toggleFs, label: lang === "es" ? "Pantalla completa" : "Fullscreen" },
    { key: "share", icon: Share2, action: share, label: lang === "es" ? "Compartir" : "Share" },
  ];

  return (
    <div className="fixed inset-0 z-[80] bg-[#05070d] text-white overflow-hidden select-none">
      <div ref={containerRef} className="absolute inset-0 cursor-grab active:cursor-grabbing"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} data-testid="panorama">
        <div className="absolute inset-y-0 left-0" style={{ width: "150%", transform: `translateX(${offset}px) scale(${zoom})`, transformOrigin: "center" }}>
          <AnimatePresence mode="popLayout">
            <motion.img key={scene.id} src={imgFallback(scene.img)} alt={L(scene.name)} draggable={false}
              initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeInOut" }} className="absolute inset-0 w-full h-full object-cover" />
          </AnimatePresence>
          {scene.hotspots.map((h, i) => {
            const target = scenes.find((s) => s.id === h.to);
            if (!target) return null;
            return (
              <button key={i} onClick={() => goScene(h.to)} className="absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${h.x}%`, top: `${h.y}%` }} data-testid={`hotspot-${h.to}`}>
                <span className="relative flex flex-col items-center">
                  <span className="absolute -top-9 whitespace-nowrap text-[0.62rem] tracking-[0.2em] uppercase font-medium opacity-90 drop-shadow">
                    {lang === "es" ? "Ir a" : "Go to"} {L(target.name)}
                  </span>
                  <span className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/90 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <img src={imgFallback(target.img)} alt="" className="w-full h-full object-cover" />
                  </span>
                  <span className="absolute inset-0 w-16 h-16 rounded-full border-2 border-white/70 animate-ping" />
                </span>
              </button>
            );
          })}
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 200px rgba(0,0,0,0.5)" }} />
      </div>

      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-5 lg:px-8 py-4 bg-gradient-to-b from-black/60 to-transparent">
        <Link to={id ? `/property/${id}` : "/"} className="flex items-center gap-3 group" data-testid="tour-exit">
          <span className="w-9 h-9 grid place-items-center border border-white/30 group-hover:border-white transition-colors"><ArrowLeft size={17} /></span>
          <span className="bg-white/95 rounded-sm px-2 py-1"><Logo className="h-7" /></span>
        </Link>
        <div className="flex items-center gap-3">
          {listings.length > 0 && (
            <select value={id || ""} onChange={(e) => e.target.value && navigate(`/virtual-tour/${e.target.value}`)}
              className="bg-black/55 backdrop-blur border border-white/25 text-white text-xs px-3 py-2 rounded-sm max-w-[220px] focus:border-brandgreen outline-none [color-scheme:dark]"
              data-testid="tour-listing-select" title={lang === "es" ? "Elegir propiedad" : "Choose property"}>
              <option value="" disabled>{lang === "es" ? "Elige una propiedad…" : "Choose a property…"}</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{L(l.title)}</option>)}
            </select>
          )}
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase">
            <Compass size={15} className="text-brandgreen" />
            <span className="opacity-80">{L(scene.name)}</span>
            <span className="opacity-40">· {idx + 1}/{scenes.length}</span>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showInfo && (
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            className="absolute top-24 left-5 lg:left-8 z-30 w-[300px] bg-white/10 backdrop-blur-xl border border-white/20 p-6" data-testid="welcome-card">
            <div className="w-10 h-0.5 bg-brandgreen mb-4" />
            <p className="text-[0.62rem] tracking-[0.25em] uppercase text-white/70 mb-1">{lang === "es" ? "Bienvenido a" : "Welcome to"}</p>
            <h1 className="font-serif text-4xl leading-none">{residence.name}</h1>
            <p className="text-xs text-white/70 mt-2">{residence.tag}</p>
            <p className="text-sm font-light text-white/85 mt-4 leading-relaxed line-clamp-4">{residence.desc}</p>
            <p className="font-serif text-2xl text-brandgreen mt-4">{residence.price}</p>
            <button onClick={() => setPanel("schedule")} className="w-full mt-4 flex items-center justify-center gap-2 text-xs tracking-wide uppercase py-3 bg-brandgreen text-white hover:brightness-110 transition-[filter]" data-testid="tour-schedule-visit">
              <Calendar size={15} /> {lang === "es" ? "Agendar Visita" : "Schedule a Visit"}
            </button>
            <div className="flex gap-2 mt-2">
              <Link to={`/property/${residence.listingId}`} className="flex-1 text-center text-xs tracking-wide uppercase py-2.5 bg-white text-[#0b142d] hover:bg-brandgreen hover:text-white transition-colors" data-testid="tour-view-listing">
                {lang === "es" ? "Ver Listado" : "View Listing"}
              </Link>
              <Link to="/contact" className="w-11 grid place-items-center py-2.5 border border-white/30 hover:border-white transition-colors"><Phone size={15} /></Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute right-5 lg:right-8 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2">
        <button onClick={() => setZoom((z) => Math.min(1.8, +(z + 0.2).toFixed(2)))} className="w-11 h-11 grid place-items-center bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 transition-colors" data-testid="zoom-in"><Plus size={17} /></button>
        <button onClick={() => { setZoom(1); setOffset(0); }} className="w-11 h-11 grid place-items-center bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 transition-colors" data-testid="zoom-reset"><RotateCw size={16} /></button>
        <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)))} className="w-11 h-11 grid place-items-center bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 transition-colors" data-testid="zoom-out"><Minus size={17} /></button>
      </div>

      <button onClick={() => { setAuto(false); setOffset((o) => Math.min(0, o + 160)); }} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 grid place-items-center bg-black/30 backdrop-blur hover:bg-black/50 transition-colors rounded-full" data-testid="pan-left"><ChevronLeft size={24} /></button>
      <button onClick={() => { setAuto(false); setOffset((o) => Math.max(bounds(), o - 160)); }} className="absolute right-20 lg:right-24 top-1/2 -translate-y-1/2 z-20 w-12 h-12 grid place-items-center bg-black/30 backdrop-blur hover:bg-black/50 transition-colors rounded-full" data-testid="pan-right"><ChevronRight size={24} /></button>

      <div className="absolute bottom-6 left-5 lg:left-8 z-30 hidden sm:block" data-testid="scene-strip">
        <p className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60 mb-2">{lang === "es" ? "Explora aquí" : "Discover here"}</p>
        <div className="flex gap-2">
          {scenes.map((s, i) => (
            <button key={s.id} onClick={() => goScene(s.id)}
              className={`relative w-16 h-12 overflow-hidden border-2 transition-all duration-300 ${i === idx ? "border-brandgreen scale-105" : "border-white/30 hover:border-white/70 opacity-80 hover:opacity-100"}`}
              data-testid={`scene-thumb-${s.id}`} title={L(s.name)}>
              <img src={imgFallback(s.img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30" data-testid="command-center">
        <div className="flex items-center gap-1 bg-black/45 backdrop-blur-xl border border-white/15 px-2 py-2 rounded-full shadow-2xl">
          {cmd.map((c) => (
            <button key={c.key} onClick={c.action} title={c.label}
              className={`group relative w-11 h-11 rounded-full grid place-items-center transition-colors duration-300 ${c.on ? "bg-brandgreen text-white" : "text-white/80 hover:bg-white/15"}`}
              data-testid={`cmd-${c.key}`}>
              <c.icon size={18} />
              <span className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-[0.6rem] tracking-wide uppercase bg-black/80 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">{c.label}</span>
            </button>
          ))}
        </div>
      </div>

      <button onClick={() => setPanel(panel === "settings" ? null : "settings")} className="absolute bottom-6 right-5 lg:right-8 z-30 w-12 h-12 rounded-full bg-black/45 backdrop-blur-xl border border-white/15 grid place-items-center hover:bg-white/15 transition-colors" data-testid="cmd-settings">
        <Settings size={19} />
      </button>

      <AnimatePresence>
        {panel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm grid place-items-center p-6" onClick={() => setPanel(null)}>
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              className="bg-[#0b1020] border border-white/15 w-full max-w-4xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <h3 className="font-serif text-2xl">
                  {panel === "scenes" && (lang === "es" ? "Espacios de la residencia" : "Residence spaces")}
                  {panel === "gallery" && (lang === "es" ? "Galería" : "Gallery")}
                  {panel === "floorplan" && (lang === "es" ? "Plano de planta" : "Floor plan")}
                  {panel === "settings" && (lang === "es" ? "Ajustes del tour" : "Tour settings")}
                  {panel === "schedule" && (lang === "es" ? "Agendar una visita" : "Schedule a visit")}
                </h3>
                <button onClick={() => setPanel(null)} className="w-9 h-9 grid place-items-center hover:bg-white/10 rounded-full" data-testid="panel-close"><X size={18} /></button>
              </div>
              <div className="p-6">
                {(panel === "scenes" || panel === "gallery") && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {scenes.map((s, i) => (
                      <button key={s.id} onClick={() => { goScene(s.id); setPanel(null); }}
                        className="group relative aspect-[4/3] overflow-hidden border border-white/10 hover:border-brandgreen transition-colors" data-testid={`panel-scene-${s.id}`}>
                        <img src={imgFallback(s.img)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        <span className="absolute bottom-2 left-3 text-sm tracking-wide">{L(s.name)}</span>
                        {i === idx && <span className="absolute top-2 right-2 text-[0.55rem] tracking-widest uppercase bg-brandgreen px-2 py-0.5">{lang === "es" ? "Aquí" : "Here"}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {panel === "floorplan" && (
                  <div className="relative aspect-[16/10] bg-[#0f1526] border border-white/10 overflow-hidden">
                    <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
                    {scenes.map((s, i) => {
                      const r = POS[i % POS.length];
                      return (
                        <button key={s.id} onClick={() => { goScene(s.id); setPanel(null); }}
                          className={`absolute border text-[0.6rem] tracking-wide uppercase grid place-items-center transition-colors px-1 text-center ${scene.id === s.id ? "bg-brandgreen/30 border-brandgreen text-white" : "bg-white/5 border-white/20 hover:bg-white/10 text-white/70"}`}
                          style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%` }} data-testid={`plan-${s.id}`}>
                          {L(s.name)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {panel === "settings" && (
                  <div className="space-y-5">
                    <label className="flex items-center justify-between"><span className="text-sm">{lang === "es" ? "Rotación automática" : "Auto-rotate"}</span>
                      <button onClick={() => setAuto((v) => !v)} className={`w-12 h-6 rounded-full transition-colors relative ${auto ? "bg-brandgreen" : "bg-white/20"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${auto ? "left-6" : "left-0.5"}`} /></button>
                    </label>
                    <label className="flex items-center justify-between"><span className="text-sm">{lang === "es" ? "Sonido ambiente" : "Ambient sound"}</span>
                      <button onClick={() => setSound((v) => !v)} className={`w-12 h-6 rounded-full transition-colors relative ${sound ? "bg-brandgreen" : "bg-white/20"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${sound ? "left-6" : "left-0.5"}`} /></button>
                    </label>
                    <button onClick={() => { setZoom(1); setOffset(0); setPanel(null); }} className="w-full py-2.5 border border-white/20 hover:bg-white/10 transition-colors text-sm tracking-wide uppercase">{lang === "es" ? "Restablecer vista" : "Reset view"}</button>
                    <p className="text-xs text-white/50">{lang === "es" ? "Tour de demostración · imágenes de alta resolución." : "Demo tour · high-resolution imagery."}</p>
                  </div>
                )}
                {panel === "schedule" && (
                  <div className="max-w-md mx-auto">
                    <p className="text-sm text-white/70 mb-5 leading-relaxed">
                      {lang === "es"
                        ? `Deja tus datos y nuestro equipo coordinará una visita a ${residence.name}.`
                        : `Leave your details and our team will arrange a visit to ${residence.name}.`}
                    </p>
                    <form onSubmit={submitVisit} className="space-y-3" data-testid="tour-schedule-form">
                      <TourField label={lang === "es" ? "Nombre" : "Name"} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tour-visit-name" required />
                      <TourField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="tour-visit-email" required />
                      <TourField label={lang === "es" ? "Teléfono" : "Phone"} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="tour-visit-phone" />
                      <div className="grid grid-cols-2 gap-3">
                        <TourField label={lang === "es" ? "Fecha" : "Date"} type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="tour-visit-date" required />
                        <TourField label={lang === "es" ? "Hora" : "Time"} type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} testid="tour-visit-time" required />
                      </div>
                      <button disabled={submitting} className="w-full py-3 bg-brandgreen text-white text-xs tracking-wide uppercase hover:brightness-110 transition-[filter] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="tour-visit-submit">
                        {submitting ? <Loader2 size={15} className="animate-spin" /> : <Calendar size={15} />}
                        {submitting ? (lang === "es" ? "Enviando..." : "Sending...") : (lang === "es" ? "Solicitar Visita" : "Request Visit")}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 text-[0.6rem] tracking-[0.25em] uppercase text-white/40 pointer-events-none hidden md:block">
        {lang === "es" ? "Arrastra para mirar alrededor" : "Drag to look around"}
      </div>
    </div>
  );
}

function TourField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] tracking-[0.2em] uppercase text-white/60 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-brandgreen focus:outline-none transition-colors [color-scheme:dark]"
      />
    </label>
  );
}
